import * as yup from 'yup';
import { TShippingFormData, TDeliveryMethod } from '../../Checkout.types';
import type { TLocalize } from 'store/contexts/LocalizationContext/LocalizationContext.types';
import {
  MAX_DEFAULT_BIG_FIELD_SYMBOLS,
  MAX_DEFAULT_FIELD_SYMBOLS,
} from 'constants/general';

const shippingDefaultMaxMessage = (localize: TLocalize, messageKey: string) =>
  localize(messageKey, [String(MAX_DEFAULT_FIELD_SYMBOLS)]) as string;

export const getShippingSchema = (localize: TLocalize) =>
  yup.object({
    firstName: yup
      .string()
      .required(localize('validation.shipping.first.name.required') as string)
      .min(2, localize('validation.shipping.first.name.min') as string)
      .max(
        MAX_DEFAULT_FIELD_SYMBOLS,
        shippingDefaultMaxMessage(
          localize,
          'validation.shipping.first.name.max'
        )
      ),
    lastName: yup
      .string()
      .required(localize('validation.shipping.last.name.required') as string)
      .min(2, localize('validation.shipping.last.name.min') as string)
      .max(
        MAX_DEFAULT_FIELD_SYMBOLS,
        shippingDefaultMaxMessage(localize, 'validation.shipping.last.name.max')
      ),
    email: yup
      .string()
      .required(localize('validation.shipping.email.required') as string)
      .email(localize('validation.shipping.email.invalid') as string)
      .max(
        MAX_DEFAULT_FIELD_SYMBOLS,
        shippingDefaultMaxMessage(localize, 'validation.shipping.email.max')
      ),
    phone: yup
      .string()
      .required(localize('validation.shipping.phone.required') as string)
      .max(
        MAX_DEFAULT_FIELD_SYMBOLS,
        shippingDefaultMaxMessage(localize, 'validation.shipping.phone.max')
      )
      .matches(
        /^[0-9]{9}$/,
        localize('validation.shipping.phone.format') as string
      ),
    street: yup
      .string()
      .required(localize('validation.shipping.street.required') as string)
      .min(3, localize('validation.shipping.street.min') as string)
      .max(
        MAX_DEFAULT_FIELD_SYMBOLS,
        shippingDefaultMaxMessage(localize, 'validation.shipping.street.max')
      ),
    buildingNumber: yup
      .string()
      .required(
        localize('validation.shipping.building.number.required') as string
      )
      .matches(
        /^[0-9]+[A-Za-z]?$/,
        localize('validation.shipping.building.number.format') as string
      )
      .max(
        MAX_DEFAULT_FIELD_SYMBOLS,
        shippingDefaultMaxMessage(
          localize,
          'validation.shipping.building.number.max'
        )
      ),
    apartmentNumber: yup
      .string()
      .max(
        MAX_DEFAULT_FIELD_SYMBOLS,
        shippingDefaultMaxMessage(
          localize,
          'validation.shipping.apartment.number.max'
        )
      )
      .optional(),
    city: yup
      .string()
      .required(localize('validation.shipping.city.required') as string)
      .min(2, localize('validation.shipping.city.min') as string)
      .max(
        MAX_DEFAULT_FIELD_SYMBOLS,
        shippingDefaultMaxMessage(localize, 'validation.shipping.city.max')
      ),
    postalCode: yup
      .string()
      .required(localize('validation.shipping.postal.code.required') as string)
      .max(
        MAX_DEFAULT_FIELD_SYMBOLS,
        shippingDefaultMaxMessage(
          localize,
          'validation.shipping.postal.code.max'
        )
      )
      .matches(
        /^[0-9]{2}-[0-9]{3}$/,
        localize('validation.shipping.postal.code.format') as string
      ),
    additionalInfo: yup
      .string()
      .max(
        MAX_DEFAULT_BIG_FIELD_SYMBOLS,
        localize('validation.shipping.additional.info.max', [
          String(MAX_DEFAULT_BIG_FIELD_SYMBOLS),
        ]) as string
      )
      .optional(),
    deliveryMethod: yup
      .mixed<TDeliveryMethod>()
      .oneOf(
        ['inpost'],
        localize('validation.shipping.delivery.method.oneof') as string
      )
      .required(
        localize('validation.shipping.delivery.method.required') as string
      ),
    parcelLockerCode: yup
      .string()
      .transform((v) => (typeof v === 'string' ? v.trim() : v))
      .when('deliveryMethod', {
        is: 'inpost',
        then: (schema) =>
          schema
            .required(
              localize(
                'validation.shipping.parcel.locker.code.required'
              ) as string
            )
            .min(
              4,
              localize('validation.shipping.parcel.locker.code.min') as string
            )
            .max(
              MAX_DEFAULT_FIELD_SYMBOLS,
              shippingDefaultMaxMessage(
                localize,
                'validation.shipping.parcel.locker.code.max'
              )
            ),
        otherwise: (schema) => schema.optional(),
      }),
  }) as yup.ObjectSchema<TShippingFormData>;
